import { ChevronDown, FolderOpen } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";
import {
  partitionProjectsByStatus,
  shouldSplitByStatus,
  type ProjectStatus,
} from "@/domains/projects/status/projectStatus";
import { ProjectStatusBadge } from "@/domains/projects/status/ProjectStatusBadge";
import { useProjectStatusEnabled } from "@/domains/projects/status/projectStatusAvailability";

export interface Channel {
  id: number;
  name: string;
  status: ProjectStatus;
}

interface ChannelDropdownProps {
  channels: Channel[];
  selectedId: number | null;
  onSelect: (id: number) => void;
}

/** Compact project-channel picker for the widget header (`# Project ▾`).
 *  Each channel carries its project's status dot; past the split threshold the
 *  inactive ones also sit under their own label below the active ones — a menu
 *  has no room for a filter control. */
export function ChannelDropdown({ channels, selectedId, onSelect }: ChannelDropdownProps) {
  const { t } = useLanguage();
  const statusEnabled = useProjectStatusEnabled();
  const selected = channels.find((c) => c.id === selectedId);
  const { active, inactive } = partitionProjectsByStatus(channels);
  const split = shouldSplitByStatus(channels.length, statusEnabled);

  const renderItem = (c: Channel) => (
    <DropdownMenuItem
      key={c.id}
      onSelect={() => onSelect(c.id)}
      className={cn("gap-1.5 text-xs", c.id === selectedId && "bg-accent")}
    >
      <FolderOpen className="h-3.5 w-3.5 flex-shrink-0 text-primary" />
      <ProjectStatusBadge variant="dot" status={c.status} />
      <span className="truncate">{c.name}</span>
    </DropdownMenuItem>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t.messages.selectChannel}
          className="inline-flex max-w-[200px] items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-transform duration-150 hover:scale-105"
        >
          <span className="inline-flex min-w-0 items-center gap-1.5 truncate">
            <FolderOpen className="h-3.5 w-3.5 flex-shrink-0 text-primary" />
            <span className="truncate">{selected?.name ?? "—"}</span>
          </span>
          <ChevronDown className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-72 w-56 overflow-y-auto">
        {!split ? (
          [...active, ...inactive].map(renderItem)
        ) : (
          <>
            {active.length > 0 && (
              <>
                <DropdownMenuLabel className="text-xs text-muted-foreground">
                  {t.projects.status.active}
                </DropdownMenuLabel>
                {active.map(renderItem)}
                {inactive.length > 0 && <DropdownMenuSeparator />}
              </>
            )}
            {inactive.length > 0 && (
              <>
                <DropdownMenuLabel className="text-xs text-muted-foreground">
                  {t.projects.status.inactive}
                </DropdownMenuLabel>
                {inactive.map(renderItem)}
              </>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
